import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { getAssignatedExecutives } from 'lib/api/business';
import { getFinanciableInvoices } from 'lib/api/dashboard';
import { FinanciableInvoicesRow } from 'lib/api/dashboard/types';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

const LastInvoices = () => {
  const dispatch = useDispatch();
  const [filteredInvoices, setFilteredInvoices] = useState<
    (FinanciableInvoicesRow & { id: string })[]
  >([]);

  const [loading, setLoading] = useState(false);
  const [allBusinesses, setAllBusinesses] = useState<any>([]);
  const [allStakeholders, setAllStakeholders] = useState<any>([]);
  const [allExecutives, setAllExecutives] = useState<any>([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [selectedStakeholder, setSelectedStakeholder] = useState('');
  const [selectedExecutive, setSelectedExecutive] = useState('');
  const [openExecutive, setOpenExecutive] = useState(false);
  const [executives, setExecutives] = useState([]);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loadingExecutives, setLoadingExecutives] = useState(false);
  const [selectedBusinessForExecutives, setSelectedBusinessForExecutives] =
    useState('');

  const { data: financiableInvoices } = getFinanciableInvoices(dispatch);

  const parseRelations = async () => {
    if (!financiableInvoices) return;

    setFilteredInvoices(
      financiableInvoices.map((i) => ({
        ...i,
        id: `${i.businessIdentifier}-${i.folio}-${i.code}`,
      })),
    );

    const businesses = new Set();
    const stakeholders = new Set();
    const executives = new Set();

    financiableInvoices.forEach((invoice) => {
      businesses.add(invoice.businessName);
      stakeholders.add(invoice.stakeholderName);
      executives.add(invoice.executiveName);
    });

    setAllBusinesses([...businesses]);
    setAllStakeholders([...stakeholders]);
    setAllExecutives([...executives]);
    setLoading(false);
  };

  const handleChange = (value: string | null, field: string) => {
    if (field === 'business') setSelectedBusiness(value ?? '');
    if (field === 'stakeholder') setSelectedStakeholder(value ?? '');
    if (field === 'executive') setSelectedExecutive(value ?? '');
    updateFilters();
  };

  const updateFilters = () => {
    if (!financiableInvoices) return;
    let filtered = [...financiableInvoices];

    if (selectedBusiness) {
      filtered = filtered.filter(
        (invoice) => invoice.businessName === selectedBusiness,
      );
    }
    if (selectedStakeholder) {
      filtered = filtered.filter(
        (invoice) => invoice.stakeholderName === selectedStakeholder,
      );
    }

    if (selectedExecutive) {
      filtered = filtered.filter(
        (invoice) => invoice.executiveName === selectedExecutive,
      );
    }

    setFilteredInvoices(filtered);

    const businesses = new Set();
    const stakeholders = new Set();
    const executives = new Set();

    filtered.forEach((invoice) => {
      businesses.add(invoice.businessName);
      stakeholders.add(invoice.stakeholderName);
      executives.add(invoice.executiveName);
    });

    setAllBusinesses([...businesses]);
    setAllStakeholders([...stakeholders]);
    setAllExecutives([...executives]);
  };

  const customTextField = (params: any, label: string) => (
    <TextField
      {...params}
      InputProps={{
        ...params.InputProps,
        style: {
          padding: '5px 10px', // ajusta el padding del input
        },
      }}
      label={label}
    />
  );

  const businessFilter = () => (
    <Autocomplete
      options={allBusinesses}
      value={selectedBusiness || null}
      getOptionLabel={(option) => option}
      renderInput={(params) => customTextField(params, 'Emisor')}
      onChange={(_event, value, _reason) => handleChange(value, 'business')}
      fullWidth
    />
  );

  const stakeholderFilter = () => (
    <Autocomplete
      options={allStakeholders}
      value={selectedStakeholder || null}
      getOptionLabel={(option) => option}
      renderInput={(params) => customTextField(params, 'Receptor')}
      onChange={(_event, value, _reason) => handleChange(value, 'stakeholder')}
      fullWidth
    />
  );

  const executiveFilter = () => (
    <Autocomplete
      options={allExecutives}
      value={selectedExecutive || null}
      getOptionLabel={(option) => option}
      renderInput={(params) => customTextField(params, 'Ejecutivo')}
      onChange={(_event, value, _reason) => handleChange(value, 'executive')}
      fullWidth
    />
  );

  useEffect(() => {
    setLoading(true);
    void parseRelations();
  }, [financiableInvoices]);

  useEffect(() => {
    updateFilters();
  }, [selectedBusiness, selectedStakeholder, selectedExecutive]);

  const fetchExecutives = async (businessIdentifier: string) => {
    setLoadingExecutives(true);
    try {
      const executivesResponse = await getAssignatedExecutives(
        dispatch,
        businessIdentifier,
      );
      setExecutives(executivesResponse);
    } catch (error) {
      console.error(error);
    }
    setLoadingExecutives(false);
  };

  const handleViewExecutive = async (invoiceId: string) => {
    if (!financiableInvoices) return;
    const invoiceSelected = financiableInvoices.find(
      (invoice) => invoice.id === invoiceId,
    );
    if (invoiceSelected) {
      setOpenExecutive(true);
      setSelectedBusinessForExecutives(invoiceSelected.businessName);
      await fetchExecutives(invoiceSelected.businessIdentifier);
    }
  };

  const canShowExecutives = () => {
    if (validatePermissions(user, 'viewBusinessExecutives')) {
      return true;
    }
    return false;
  };

  const actions = [
    ...(canShowExecutives()
      ? [
          {
            action: handleViewExecutive,
            actionLabel: 'Ver ejecutivos',
          },
        ]
      : []),
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Facturas transables en los útlimos 5 días"
        variant="body1"
        item={
          <Grid container spacing={2} style={{ minWidth: '600px' }}>
            <Grid item xs={4}>
              {businessFilter()}
            </Grid>
            <Grid item xs={4}>
              {stakeholderFilter()}
            </Grid>
            <Grid item xs={4}>
              {executiveFilter()}
            </Grid>
          </Grid>
        }
      />

      <EnhancedTable
        dataHeaders={[
          { key: 'folio', label: 'Folio' },
          { key: 'businessName', label: 'Nombre Emisor' },
          { key: 'stakeholderName', label: 'Nombre receptor' },
          { key: 'stakeholderIdentifier', label: 'Rut receptor' },
          {
            key: 'stakeholderSalesSegment',
            label: 'Ventas',
            nullValue: 'N/A',
          },
          { key: 'issuedDate', label: 'Emisión', type: 'eight-days-date' },
          { key: 'confirmDate', label: 'Confirmación', type: 'date' },
          { key: 'amount', label: 'Monto', type: 'money' },
          { key: 'executiveName', label: 'Ejecutivo' },
        ]}
        exportToFile="xlsx"
        data={filteredInvoices}
        actions={actions}
        rowsPerPageDefault={10}
        lastElement
      />
      <Dialog open={openExecutive} onClose={() => setOpenExecutive(false)}>
        <DialogTitle>{selectedBusinessForExecutives}</DialogTitle>
        {loadingExecutives ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <DialogContent>
            <Typography marginBottom={2}>
              Este cliente le pertence a los siguientes ejecutivos:
            </Typography>
            {executives.map((executive: any) => (
              <Typography key={executive.id}>
                - {executive.name} ({executive.email})
              </Typography>
            ))}
            {executives.length === 0 && (
              <Typography>
                No hay ejecutivos asignados a este cliente
              </Typography>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <CustomButton onClick={() => setOpenExecutive(false)} color="primary">
            Salir
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LastInvoices;
